import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import { runTrivecRevenueCheck, type RevenueCheckResult } from '../../../../../../services/admin/adminService';
import { ChevronDown, ChevronRight, Scale } from 'lucide-react';
import { TrivecRevenueCheckResults } from './TrivecRevenueCheckResults';
import type { TFn } from '../../types';

/**
 * Trivecgroup revenue check — a collapsible panel inside the AI Checker view.
 *
 * Scans historical trivecgroup report emails from Gmail (raw search query), diffs the report's
 * ground-truth revenue against config + stored revenue_history, and reports every issue (dry run).
 * Apply mode additively backfills fixable gaps (missing days / configured sections) in place — it
 * never overwrites or deletes stored data — and re-audits touched emails. Apply sits behind a
 * confirm dialog because it writes data.
 */
export function TrivecRevenueCheckPanel({ t }: { t: TFn }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [count, setCount] = useState('200');
  const [fileType, setFileType] = useState<'pdf' | 'xlsx' | 'productmix-multiday'>('pdf');
  const [account, setAccount] = useState<'cloud63' | 'cloud64'>('cloud63');
  const [overrideMismatches, setOverrideMismatches] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<RevenueCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (mode: 'dry' | 'apply') => {
    const q = query.trim();
    if (!q) return;
    if (mode === 'apply' && !window.confirm(t(overrideMismatches ? 'adminTesting.revenueCheck.applyOverrideConfirm' : 'adminTesting.revenueCheck.applyConfirm'))) return;
    try {
      setStatus('loading');
      setError(null);
      const parsed = parseInt(count, 10);
      const r = await runTrivecRevenueCheck(q, mode, Number.isFinite(parsed) ? parsed : undefined, fileType, overrideMismatches, account);
      setResult(r);
      setStatus('done');
    } catch (e: any) {
      setError(e.message || 'Failed to run revenue check');
      setStatus('error');
    }
  };

  const inputCls = cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);
  const loading = status === 'loading';

  return (
    <div className={cn(cardClass, 'px-3 py-4 sm:px-4')}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Scale className="w-4 h-4 shrink-0 text-purple-600 dark:text-purple-400" />
          <h3 className={cn(primaryText, semiBoldText, 'truncate')}>{t('adminTesting.revenueCheck.title')}</h3>
        </div>
        {open ? <ChevronDown className={cn('w-4 h-4 shrink-0', secondaryText)} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', secondaryText)} />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.revenueCheck.desc')}</p>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.revenueCheck.queryLabel')}</label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('adminTesting.revenueCheck.queryPlaceholder')}
                className={cn(inputCls, 'w-full')}
              />
            </div>
            <div className="w-28">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.revenueCheck.countLabel')}</label>
              <input
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className={cn(inputCls, 'w-full')}
              />
            </div>
            <div className="w-40">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.revenueCheck.fileTypeLabel')}</label>
              <select
                value={fileType}
                onChange={(e) => setFileType(e.target.value as 'pdf' | 'xlsx' | 'productmix-multiday')}
                className={cn(inputCls, 'w-full')}
              >
                <option value="pdf">{t('adminTesting.revenueCheck.fileTypePdf')}</option>
                <option value="xlsx">{t('adminTesting.revenueCheck.fileTypeXlsx')}</option>
                <option value="productmix-multiday">{t('adminTesting.revenueCheck.fileTypeProductMixMultiDay')}</option>
              </select>
            </div>
            <div className="w-36">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.revenueCheck.accountLabel')}</label>
              <select
                value={account}
                onChange={(e) => setAccount(e.target.value as 'cloud63' | 'cloud64')}
                className={cn(inputCls, 'w-full')}
              >
                <option value="cloud63">{t('adminTesting.revenueCheck.accountCloud63')}</option>
                <option value="cloud64">{t('adminTesting.revenueCheck.accountCloud64')}</option>
              </select>
            </div>
            <button
              onClick={() => run('dry')}
              disabled={loading || !query.trim()}
              className={cn('px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed', primaryText, smallTextSize, semiBoldText)}
            >
              {loading ? t('adminTesting.revenueCheck.running') : t('adminTesting.revenueCheck.dryRun')}
            </button>
            <button
              onClick={() => run('apply')}
              disabled={loading || !query.trim()}
              className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
            >
              {t('adminTesting.revenueCheck.apply')}
            </button>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={overrideMismatches}
              onChange={(e) => setOverrideMismatches(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:text-purple-600 focus:ring-purple-500 dark:focus:ring-purple-500"
            />
            <span className={cn(secondaryText, 'text-xs')}>{t('adminTesting.revenueCheck.overrideMismatchesLabel')}</span>
          </label>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
          )}

          {result && <TrivecRevenueCheckResults result={result} t={t} />}
        </div>
      )}
    </div>
  );
}
